import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  getListCoursesQueryKey,
  useCreateCourse,
  useDeleteCourse,
  useListCourses,
  useUpdateCourse,
} from '@/hooks/use-api';
import { useToast } from '@/hooks/use-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import type { Course } from '@workspace/api-client-react';
import { Edit, Loader2, MoreVertical, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

const formSchema = z.object({
  title: z.string().min(2, 'Title is required'),
  description: z.string().min(10, 'Description needs more detail'),
  level: z.enum(['beginner', 'intermediate', 'advanced'] as const),
  price: z.coerce.number().min(0, 'Price must be positive'),
  duration: z.string().min(2, "Duration is required (e.g. '2 Days')"),
  maxStudents: z.coerce.number().min(1, 'Must allow at least 1 student'),
  active: z.boolean().default(true),
});

export default function AdminCourses() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: courses, isLoading } = useListCourses({
    query: { queryKey: getListCoursesQueryKey() },
  });

  const createCourse = useCreateCourse();
  const updateCourse = useUpdateCourse();
  const deleteCourse = useDeleteCourse();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      description: '',
      level: 'beginner',
      price: 0,
      duration: '',
      maxStudents: 4,
      active: true,
    },
  });

  const openNewDialog = () => {
    setEditingCourse(null);
    form.reset({
      title: '',
      description: '',
      level: 'beginner',
      price: 0,
      duration: '',
      maxStudents: 4,
      active: true,
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (course: Course) => {
    setEditingCourse(course);
    form.reset({
      title: course.title,
      description: course.description,
      level: course.level,
      price: course.price,
      duration: course.duration,
      maxStudents: course.maxStudents,
      active: course.active,
    });
    setIsDialogOpen(true);
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      if (editingCourse) {
        await updateCourse.mutateAsync({
          id: editingCourse.id,
          data: values,
        });
        toast({ title: 'Course Updated' });
      } else {
        await createCourse.mutateAsync({
          data: values,
        });
        toast({ title: 'Course Created' });
      }
      queryClient.invalidateQueries({ queryKey: getListCoursesQueryKey() });
      setIsDialogOpen(false);
    } catch (e) {
      toast({ title: 'Error saving course', variant: 'destructive' });
    }
  };

  const handleDelete = () => {
    if (!deleteId) return;
    deleteCourse.mutate(
      { id: deleteId },
      {
        onSuccess: () => {
          toast({ title: 'Course Deleted' });
          queryClient.invalidateQueries({ queryKey: getListCoursesQueryKey() });
          setDeleteId(null);
        },
        onError: () => {
          toast({
            title: 'Error',
            description: 'Failed to delete course.',
            variant: 'destructive',
          });
          setDeleteId(null);
        },
      }
    );
  };

  const isSaving = createCourse.isPending || updateCourse.isPending;

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="font-bebas text-4xl tracking-wide mb-2">COURSES</h1>
          <p className="font-sans text-slate-500">
            Manage training programs, pricing, and availability.
          </p>
        </div>
        <Button
          onClick={openNewDialog}
          className="font-rajdhani font-bold tracking-wider"
        >
          <Plus className="w-4 h-4 mr-2" /> ADD COURSE
        </Button>
      </div>

      <Card className="border-slate-200 dark:border-slate-800 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-slate-50 dark:bg-slate-900/50">
              <TableRow>
                <TableHead className="font-rajdhani font-bold tracking-wider">
                  COURSE INFO
                </TableHead>
                <TableHead className="font-rajdhani font-bold tracking-wider">
                  LEVEL / DURATION
                </TableHead>
                <TableHead className="font-rajdhani font-bold tracking-wider">
                  PRICE
                </TableHead>
                <TableHead className="font-rajdhani font-bold tracking-wider">
                  STATUS
                </TableHead>
                <TableHead className="font-rajdhani font-bold tracking-wider text-right">
                  ACTIONS
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array(4)
                  .fill(0)
                  .map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton className="h-5 w-48" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-32" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-16" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-6 w-16 rounded-full" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-8 w-8 ml-auto" />
                      </TableCell>
                    </TableRow>
                  ))
              ) : Array.isArray(courses) && courses.length > 0 ? (
                courses.map(course => (
                  <TableRow key={course.id} className="font-sans">
                    <TableCell>
                      <div className="font-bebas text-xl tracking-wide">
                        {course.title}
                      </div>
                      <div className="text-sm text-slate-500 max-w-md truncate">
                        {course.description}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="capitalize font-medium">
                        {course.level}
                      </div>
                      <div className="text-xs text-slate-500">
                        {course.duration}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-bold text-primary">
                        ${course.price}
                      </div>
                      <div className="text-xs text-slate-500">
                        Max {course.maxStudents} students
                      </div>
                    </TableCell>
                    <TableCell>
                      {course.active ? (
                        <Badge className="bg-green-500">ACTIVE</Badge>
                      ) : (
                        <Badge variant="secondary">INACTIVE</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="font-rajdhani font-bold tracking-wider"
                        >
                          <DropdownMenuItem
                            onClick={() => openEditDialog(course)}
                          >
                            <Edit className="mr-2 h-4 w-4" /> Edit Details
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setDeleteId(course.id)}
                            className="text-red-600 focus:text-red-600 mt-2 border-t border-slate-100 dark:border-slate-800"
                          >
                            <Trash2 className="mr-2 h-4 w-4" /> Delete Course
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-10 text-slate-500 font-sans"
                  >
                    No courses found. Create one to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="font-bebas text-3xl tracking-wider">
              {editingCourse ? 'EDIT COURSE' : 'CREATE NEW COURSE'}
            </DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-rajdhani font-bold tracking-wider">
                      COURSE TITLE
                    </FormLabel>
                    <FormControl>
                      <Input {...field} className="font-sans" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="level"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-rajdhani font-bold tracking-wider">
                        LEVEL
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="font-sans">
                            <SelectValue placeholder="Select level" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="beginner">Beginner</SelectItem>
                          <SelectItem value="intermediate">
                            Intermediate
                          </SelectItem>
                          <SelectItem value="advanced">Advanced</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-rajdhani font-bold tracking-wider">
                        PRICE ($)
                      </FormLabel>
                      <FormControl>
                        <Input type="number" {...field} className="font-sans" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="duration"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-rajdhani font-bold tracking-wider">
                        DURATION
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. 2 Days"
                          {...field}
                          className="font-sans"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="maxStudents"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-rajdhani font-bold tracking-wider">
                        MAX STUDENTS
                      </FormLabel>
                      <FormControl>
                        <Input type="number" {...field} className="font-sans" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-rajdhani font-bold tracking-wider">
                      DESCRIPTION
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        className="font-sans h-24 resize-none"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border border-slate-200 dark:border-slate-800 p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base font-rajdhani font-bold tracking-wider">
                        ACTIVE STATUS
                      </FormLabel>
                      <div className="text-sm font-sans text-slate-500">
                        Course will be visible to students for booking.
                      </div>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <DialogFooter className="pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="font-rajdhani font-bold tracking-wider"
                >
                  CANCEL
                </Button>
                <Button
                  type="submit"
                  disabled={isSaving}
                  className="font-rajdhani font-bold tracking-wider"
                >
                  {isSaving && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  {editingCourse ? 'SAVE CHANGES' : 'CREATE COURSE'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteId}
        onOpenChange={open => !open && setDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-bebas text-2xl tracking-wider">
              DELETE COURSE?
            </AlertDialogTitle>
            <AlertDialogDescription className="font-sans">
              This action cannot be undone. This will permanently delete the
              course. Note that appointments linked to this course might be
              affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-rajdhani font-bold tracking-wider">
              CANCEL
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 font-rajdhani font-bold tracking-wider"
            >
              DELETE
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
